import React, { useState, useEffect } from "react";
import payrollService from "../../services/payrollService";
import Notification from "../../components/UI/Notification";
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import ReactPaginate from "react-paginate";

const PayrollConfiguration = () => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [form, setForm] = useState({
    component_name: "",
    component_type: "earning",
    calculation_type: "fixed",
    default_value: "",
    percentage: "",
    formula: "",
    is_taxable: true,
    tax_percentage: "",
    is_active: true,
    display_order: 1,
    description: "",
  });

  useEffect(() => {
    fetchComponents(1);
  }, [filterType]);

  const fetchComponents = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (filterType) params.component_type = filterType;
      const response = await payrollService.getAllConfigurations(params);

      // Support both: paginated { data: { data: [...], current_page, last_page, ... } } and flat { data: [...] }
      const inner = response?.data;
      let list = Array.isArray(inner?.data) ? inner.data : Array.isArray(inner) ? inner : [];
      if (!Array.isArray(list)) list = [];

      if (filterType && list.length > 0) {
        list = list.filter((c) => c.component_type === filterType);
      }
      list.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      list.reverse();
      setComponents(list);

      const paginationSource = inner && !Array.isArray(inner) ? inner : response || {};
      setPagination({
        totalPages: paginationSource.last_page ?? 1,
        totalItems: paginationSource.total ?? list.length,
        perPage: paginationSource.per_page ?? 15,
      });
      setCurrentPage(paginationSource.current_page ?? page);
    } catch (error) {
      setNotification({ type: "error", message: "Failed to fetch components" });
      setComponents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Prepare payload based on calculation type
      const payload = {
        ...form,
        default_value:
          form.calculation_type === "fixed" ? form.default_value : null,
        percentage:
          form.calculation_type === "percentage" ? form.percentage : null,
        tax_percentage: form.is_taxable ? form.tax_percentage : null,
      };

      if (editingId) {
        await payrollService.updateConfiguration(editingId, payload);
        setNotification({
          type: "success",
          message: "Component updated successfully",
        });
      } else {
        await payrollService.createConfiguration(payload);
        setNotification({
          type: "success",
          message: "Component created successfully",
        });
      }
      setShowModal(false);
      resetForm();
      fetchComponents(currentPage);
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Operation failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this component?")) return;
    try {
      await payrollService.deleteConfiguration(id);
      setNotification({
        type: "success",
        message: "Component deleted successfully",
      });
      fetchComponents(currentPage);
    } catch (error) {
      setNotification({ type: "error", message: "Failed to delete component" });
    }
  };

  const resetForm = () => {
    setForm({
      component_name: "",
      component_type: "earning",
      calculation_type: "fixed",
      default_value: "",
      percentage: "",
      formula: "",
      is_taxable: true,
      tax_percentage: "",
      is_active: true,
      display_order: components.length + 1,
      description: "",
    });
    setEditingId(null);
  };

  const openEditModal = (component) => {
    setForm({
      component_name: component.component_name,
      component_type: component.component_type,
      calculation_type: component.calculation_type,
      default_value: component.default_value || "",
      percentage: component.percentage || "",
      formula: component.formula || "",
      is_taxable: component.is_taxable,
      // Use nullish coalescing so 0 / "0" doesn't get lost, and fall back to alternate keys if API changed
      tax_percentage:
        component.tax_percentage ??
        component.tax_percent ??
        component.tax_rate ??
        component.taxable_percentage ??
        "",
      is_active: component.is_active,
      display_order: component.display_order,
      description: component.description || "",
    });
    setEditingId(component.id);
    setShowModal(true);
  };

  const getTypeColor = (type) => {
    const colors = {
      earning:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      deduction: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      variable: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    };
    return colors[type] || colors.earning;
  };

  const getCalculationDisplay = (component) => {
    if (component.calculation_type === "fixed" && component.default_value) {
      return `₹${parseFloat(component.default_value).toLocaleString("en-IN")}`;
    } else if (
      component.calculation_type === "percentage" &&
      component.percentage
    ) {
      return `${component.percentage}%`;
    } else if (component.formula) {
      return component.formula;
    }
    return "Variable";
  };

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
            Payroll Components
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage salary components (earnings, deductions, variables)
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
        >
          <Plus size={18} />
          Add Component
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {["", "earning", "deduction", "variable"].map((type) => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              filterType === type
                ? "bg-blue-600 text-white"
                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            {type === ""
              ? "All Components"
              : type.charAt(0).toUpperCase() + type.slice(1) + "s"}
          </button>
        ))}
      </div>

      {/* Components List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Sr. No.
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Component Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Calculation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Taxable
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : components.length > 0 ? (
                components.map((component, index) => (
                  <tr
                    key={component.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {(currentPage - 1) * pagination.perPage + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {component.component_name}
                      </div>
                      {component.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {component.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeColor(
                          component.component_type
                        )}`}
                      >
                        {component.component_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {component.calculation_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {getCalculationDisplay(component)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {component.is_taxable ? (
                        <span className="text-green-600 dark:text-green-400 text-sm">
                          Yes
                        </span>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400 text-sm">
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {component.is_active ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(component)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(component.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    No components found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={(e) => {
                const nextPage = e.selected + 1;
                setCurrentPage(nextPage);
                fetchComponents(nextPage);
              }}
              forcePage={currentPage - 1}
              pageRangeDisplayed={5}
              pageCount={pagination.totalPages}
              previousLabel="< Prev"
              containerClassName="flex items-center justify-center gap-2 flex-wrap"
              pageClassName="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm"
              pageLinkClassName="text-gray-700 dark:text-gray-300"
              previousClassName="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-700 dark:text-gray-300"
              nextClassName="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-700 dark:text-gray-300"
              activeClassName="bg-blue-600 text-white border-blue-600"
              activeLinkClassName="text-white"
              disabledClassName="opacity-50 pointer-events-none"
            />
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
              Showing page {currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingId ? "Edit" : "Add"} Payroll Component
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Component Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.component_name}
                    onChange={(e) =>
                      setForm({ ...form, component_name: e.target.value })
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Basic Salary, HRA, PF"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Component Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.component_type}
                    onChange={(e) =>
                      setForm({ ...form, component_type: e.target.value })
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="earning">Earning</option>
                    <option value="deduction">Deduction</option>
                    <option value="variable">Variable</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Calculation Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.calculation_type}
                    onChange={(e) =>
                      setForm({ ...form, calculation_type: e.target.value })
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="fixed">Fixed Amount</option>
                    <option value="percentage">Percentage</option>
                    <option value="formula">Formula Based</option>
                  </select>
                </div>

                {/* Show appropriate field based on calculation type */}
                {form.calculation_type === "fixed" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Default Value (₹)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.default_value}
                      onChange={(e) =>
                        setForm({ ...form, default_value: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., 1250.00"
                    />
                  </div>
                )}

                {form.calculation_type === "percentage" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.percentage}
                      onChange={(e) =>
                        setForm({ ...form, percentage: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., 50.00"
                    />
                  </div>
                )}

                {form.calculation_type === "formula" && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Formula
                    </label>
                    <input
                      type="text"
                      value={form.formula}
                      onChange={(e) =>
                        setForm({ ...form, formula: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., basic_salary * 0.5"
                    />
                  </div>
                )}

                {form.is_taxable && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tax Percentage (%) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.tax_percentage}
                      onChange={(e) =>
                        setForm({ ...form, tax_percentage: e.target.value })
                      }
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., 10.00"
                    />
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Brief description of the component"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.is_taxable}
                      onChange={(e) =>
                        setForm({ ...form, is_taxable: e.target.checked })
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Taxable
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) =>
                        setForm({ ...form, is_active: e.target.checked })
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Active
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollConfiguration;
